// SEQ-228: how the schedule check modal describes a check that did not finish.
//
// A failed check (status 'failed', or a stored result that carries an error)
// used to render as "Discrepancy", which tells the user the schedule disagrees
// with the claim when in fact nothing was compared. These pure functions decide
// the label, the date line and whether the retry button shows, so the rules
// can be tested without a DOM.

export interface ScheduleCheckResultLike {
  error?: string | null;
  analyzed_at?: string | null;
  overall_status?: string | null;
}

/** True when the check never produced a comparison. */
export function isFailedScheduleCheck(
  result: ScheduleCheckResultLike | null | undefined,
  status?: string | null,
): boolean {
  if (status === "failed") return true;
  const error = result?.error;
  return typeof error === "string" && error.trim() !== "";
}

export const FAILED_CHECK_LABEL = "Check did not finish";

/** The label shown in the modal header next to "Status:". */
export function scheduleCheckStatusLabel(
  failed: boolean,
  overallStatus: string | null | undefined,
): string {
  if (failed) return FAILED_CHECK_LABEL;
  if (overallStatus === "match") return "All Match";
  if (overallStatus === "partial") return "Partial Match";
  return "Discrepancy";
}

/**
 * The line under the title. A finished check says when it was analysed. A
 * failed check never says "Analyzed": it uses the time recorded in the result
 * when there is one, and otherwise says it did not finish.
 */
export function scheduleCheckDateLine(
  failed: boolean,
  analyzedAt: string | null | undefined,
  result: ScheduleCheckResultLike | null | undefined,
  formatDate: (iso: string) => string,
): string {
  if (!failed) {
    return analyzedAt ? `Analysed ${formatDate(analyzedAt)}` : "Analysed, date not recorded";
  }
  const when = result?.analyzed_at || analyzedAt;
  return when ? `Did not finish on ${formatDate(when)}` : "Did not finish";
}

/**
 * The retry button needs a stored schedule to run against and a handler. A
 * finished check does not offer it: the user re-uploads to change the answer.
 */
export function canRunScheduleCheckAgain(
  failed: boolean,
  paymentScheduleUrl: string | null | undefined,
  hasHandler: boolean,
): boolean {
  return (
    failed &&
    hasHandler &&
    typeof paymentScheduleUrl === "string" &&
    paymentScheduleUrl.trim() !== ""
  );
}

// Starting a check (SEQ-228 round 2)

export type StartFailureKind = "network" | "auth" | "timeout" | "other";

export interface StartFailureDecision {
  // Stop showing the spinner for this claim
  clearSpinner: boolean;
  // Close the Realtime channel opened for this claim
  unsubscribe: boolean;
  // Plain-words error toast, or null to stay quiet
  toast: { title: string; message: string } | null;
}

function httpStatusOf(err: unknown): number | null {
  if (typeof err !== "object" || err === null) return null;
  const context = (err as { context?: unknown }).context;
  const status =
    typeof context === "object" && context !== null
      ? (context as { status?: unknown }).status
      : (err as { status?: unknown }).status;
  return typeof status === "number" ? status : null;
}

/**
 * What kind of failure did supabase.functions.invoke report? A rejection or a
 * FunctionsFetchError has no HTTP status and means the request never arrived.
 */
export function classifyStartFailure(err: unknown): StartFailureKind {
  const status = httpStatusOf(err);
  if (status === 401 || status === 403) return "auth";
  if (status === 408 || status === 504) return "timeout";
  if (status !== null) return "other";
  const name = typeof err === "object" && err !== null ? (err as { name?: unknown }).name : null;
  return name === "FunctionsHttpError" || name === "FunctionsRelayError" ? "other" : "network";
}

const START_FAILED_TITLE = "Schedule check did not start";

/**
 * Decides what to do when the request to start a check fails. A gateway
 * timeout is the one case that is not a failure to start: the function can
 * still be running and will write its result, so the spinner and channel stay
 * until Realtime reports. If Realtime has already reported the end of this
 * check (for example the function refused and marked the row failed), the
 * spinner is already cleared and a second toast would only repeat it.
 */
export function decideStartFailure(err: unknown, alreadySettled: boolean): StartFailureDecision {
  if (alreadySettled) return { clearSpinner: false, unsubscribe: false, toast: null };
  const kind = classifyStartFailure(err);
  if (kind === "timeout") return { clearSpinner: false, unsubscribe: false, toast: null };
  const message =
    kind === "network"
      ? "We could not reach the server. Check your connection, then run the check again."
      : kind === "auth"
        ? "Your sign-in may have expired. Sign in again, then run the check again."
        : "Something went wrong starting the check. Run the check again, or upload the schedule again.";
  return { clearSpinner: true, unsubscribe: true, toast: { title: START_FAILED_TITLE, message } };
}

// The instructions window, for a new upload or a re-run

export interface InstructionsWindowCopy {
  title: string;
  sourceLabel: string;
  sourceName: string;
  confirmLabel: string;
}

/**
 * Wording for the guidance window. A new upload keeps its existing wording. A
 * re-run has no new file, so it names the schedule already on file instead.
 */
export function instructionsWindowCopy(
  newFileName: string | null | undefined,
  storedFileName: string | null | undefined,
): InstructionsWindowCopy {
  if (newFileName) {
    return {
      title: "Analyze Payment Schedule",
      sourceLabel: "File Selected:",
      sourceName: newFileName,
      confirmLabel: "Analyze with AI",
    };
  }
  return {
    title: "Run the check again",
    sourceLabel: "Schedule on file:",
    sourceName: storedFileName || "the schedule already uploaded",
    confirmLabel: "Run the check",
  };
}
